import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Add request interceptor to include Authorization header
api.interceptors.request.use(
  (config) => {
    // ✅ FIXED: Check both possible token storage keys
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔑 API Request with token:', config.method.toUpperCase(), config.url);
    } else {
      console.log('⚠️ API Request without token:', config.method.toUpperCase(), config.url);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.status, response.config.url);
    
    // Debug selections endpoint specifically
    if (response.config.url && response.config.url.includes('/selections')) {
      console.log('=== SELECTIONS RESPONSE INTERCEPTOR ===');
      console.log('Raw response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Response headers:', response.headers);
      
      // Check if response.data is a string that needs parsing
      if (typeof response.data === 'string') {
        console.log('Response is string, attempting to parse...');
        try {
          const parsed = JSON.parse(response.data);
          console.log('Parsed data:', parsed);
          response.data = parsed;
        } catch (e) {
          console.error('Failed to parse string response:', e);
        }
      }
    }
    
    return response;
  },
  (error) => {
    console.error('❌ API Error:', {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data,
      message: error.message
    });
    
    // Handle 401 errors specifically
    if (error.response?.status === 401) {
      console.error('🚨 401 Unauthorized - Token may be invalid or missing');
      console.error('Current tokens:', {
        token: localStorage.getItem('token'),
        access_token: localStorage.getItem('access_token'),
        user_id: localStorage.getItem('user_id'),
        customerId: localStorage.getItem('customerId')
      });
    }
    
    return Promise.reject(error);
  }
);

export const getHousePlanOptions = async (housePlanId, customerId, location) => {
  try {
    console.log('🏠 Fetching house plan options:', { housePlanId, customerId, location });
    
    const response = await api.get(`/api/house-plans/${housePlanId}/options`, {
      params: { customerId, location },
    });
    
    const options = response.data;
    console.log('✅ House plan options received:', options.length, 'options');
    return options;
  } catch (error) {
    console.error('❌ Error fetching house plan options:', error.response?.data || error.message);
    
    // Return fallback data for development
    return [
      { optionId: 'opt-01-0001', optionName: 'Unfinished LL', location: 'Pivotal', category: 'Pivotal', housePlanOptionPrices: [{ planId: housePlanId, price: 0 }] },
      { optionId: 'opt-01-0002', optionName: 'Partial Finish LL', location: 'Pivotal', category: 'Pivotal', housePlanOptionPrices: [{ planId: housePlanId, price: 9750 }] },
      { optionId: 'opt-02-0001', optionName: '12x10 Deck', location: 'Exterior', category: 'Decks and Patios', housePlanOptionPrices: [{ planId: housePlanId, price: 1300 }] },
    ];
  }
};

export const saveSelections = async (selections) => {
  try {
    console.log('💾 Saving selections:', selections);
    
    // Ensure we have required data
    if (!selections.customerId) {
      console.error('[API.JS] Customer ID missing in selections object');
      console.error('Selections object:', selections);
      throw new Error('[API.JS FRONTEND] Customer ID is required for saving selections');
    }
    
    const response = await api.post('/customers/selections', selections);
    console.log('✅ Save selections successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error saving selections:', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      selections: selections
    });
    throw error;
  }
};

export const getCustomerSelections = async (customerId) => {
  try {
    console.log('📖 Fetching customer selections for ID:', customerId);
    
    if (!customerId) {
      console.log('⚠️ No customer ID provided, skipping selection fetch');
      return null;
    }
    
    const response = await api.get(`/customers/${customerId}/selections`);
    
    // Add detailed logging to debug empty response
    console.log('=== API RESPONSE DEBUG ===');
    console.log('Full response object:', response);
    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);
    console.log('Response data:', response.data);
    console.log('Response data type:', typeof response.data);
    console.log('Response data keys:', Object.keys(response.data || {}));
    console.log('Response data stringified:', JSON.stringify(response.data));
    
    // Check if data is wrapped in another object
    if (response.data && typeof response.data === 'object') {
      // Check for the new structured response format FIRST
      if (response.data.selections && response.data.customer && response.data.metadata) {
        console.log('Found new structured response format with customer data');
        return response.data; // Return the full structured response
      }
      
      // Common wrapper patterns (for backward compatibility)
      if (response.data.data) {
        console.log('Found data.data wrapper:', response.data.data);
        return response.data.data;
      }
      if (response.data.result) {
        console.log('Found data.result wrapper:', response.data.result);
        return response.data.result;
      }
      
      // Check if the response is the customer object with selections property
      if (response.data.customer_id !== undefined && response.data.selections) {
        console.log('Found customer object with selections:', response.data.selections);
        return { selections: response.data.selections };
      }
      
      // Check if empty object but should have data
      if (Object.keys(response.data).length === 0) {
        console.warn('⚠️ Received empty object from API');
        // Try alternate endpoint
        console.log('Attempting alternate endpoint...');
        const altResponse = await api.get(`/customers/${customerId}`);
        console.log('Alternate endpoint response:', altResponse.data);
        if (altResponse.data && altResponse.data.selections) {
          return { selections: altResponse.data.selections };
        }
      }
    }
    
    console.log('✅ Customer selections received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching customer selections:', {
      customerId,
      error: error.response?.data || error.message,
      status: error.response?.status
    });
    return null;
  }
};

export const getMarkets = async () => {
  try {
    console.log('🌍 Fetching markets...');
    const response = await api.get('/data/markets');
    console.log('✅ Markets received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching markets:', error.response?.data || error.message);
    return ['Omaha', 'OtherMarket'];
  }
};

export const getCollections = async (market) => {
  try {
    console.log('📚 Fetching collections for market:', market);
    const response = await api.get('/data/collections', { params: { market } });
    console.log('✅ Collections received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching collections:', error.response?.data || error.message);
    return ['Collection1', 'Collection2'];
  }
};

export const getHousePlans = async (market, collection) => {
  try {
    console.log('🏗️ Fetching house plans:', { market, collection });
    const response = await api.get('/data/houseplans', { params: { market, collection } });
    console.log('✅ House plans received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching house plans:', error.response?.data || error.message);
    return [{ planid: 'PL-1021', name: 'Plan 1', housePlanPricing: [{ market, baseprice: 100000 }] }];
  }
};

export const getSubdivisions = async (market) => {
  try {
    console.log('🏘️ Fetching subdivisions for market:', market);
    const response = await api.get('/data/subdivisions', { params: { market } });
    console.log('✅ Subdivisions received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching subdivisions:', error.response?.data || error.message);
    return ['Subdivision1', 'Subdivision2'];
  }
};

export const getLots = async (subdivision) => {
  try {
    console.log('📍 Fetching lots for subdivision:', subdivision);
    const response = await api.get('/data/lots', { params: { subdivision } });
    console.log('✅ Lots received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching lots:', error.response?.data || error.message);
    return [{ lotnumber: 'Lot1', buyerlotprice: 50000, lotstatus: 'Available' }];
  }
};

// ✅ NEW: Debug function to check auth state
export const debugAuthState = () => {
  const authInfo = {
    token: localStorage.getItem('token'),
    access_token: localStorage.getItem('access_token'),
    user_id: localStorage.getItem('user_id'),
    user_type: localStorage.getItem('user_type'),
    customerId: localStorage.getItem('customerId')
  };
  
  console.log('🔍 Current Auth State:', authInfo);
  
  const activeToken = authInfo.token || authInfo.access_token;
  if (activeToken) {
    console.log('✅ Active token found:', activeToken.substring(0, 10) + '...');
  } else {
    console.log('❌ No authentication token found');
  }
  
  return authInfo;
};

// ✅ NEW: Test save endpoint manually
export const testSaveEndpoint = async () => {
  const customerId = localStorage.getItem('customerId');
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  
  if (!customerId) {
    console.error('❌ No customer ID found for save test');
    return false;
  }
  
  if (!token) {
    console.error('❌ No auth token found for save test');
    return false;
  }
  
  const testData = {
    customerId: parseInt(customerId),
    selections: {
      pivotalSelections: 999,
      exterior: 555,
      selectionsBySection: { 'test-save': ['opt-test-1'] }
    }
  };
  
  try {
    console.log('🧪 Testing save endpoint with data:', testData);
    const response = await saveSelections(testData);
    console.log('✅ Save test successful:', response);
    return true;
  } catch (error) {
    console.error('❌ Save test failed:', error);
    return false;
  }
};

// ========== PHASE 2: ENHANCED API ENDPOINTS ==========

// ===== PRICING ENGINE INTEGRATION =====
export const pricingEngineAPI = {
  // Calculate price with dependencies
  calculatePriceWithDependencies: async (costItemId, selectedOptions, vendorBidPrices) => {
    const response = await api.post('/pricing-engine/calculate-dependencies', {
      costItemId,
      selectedOptions,
      vendorBidPrices,
    });
    return response.data;
  },

  // Get pricing optimization suggestions
  getOptimizationSuggestions: async (customerId, currentSelections) => {
    const response = await api.post('/pricing-engine/optimize', {
      customerId,
      currentSelections,
    });
    return response.data;
  },

  // Get dynamic pricing updates
  getDynamicPricing: async (projectParams) => {
    const response = await api.post('/pricing-engine/dynamic-pricing', projectParams);
    return response.data;
  },
};

// ===== OPERATIONS MANAGEMENT =====
export const operationsAPI = {
  // Project management
  getProjects: async (filters = {}) => {
    const response = await api.get('/operations/projects', { params: filters });
    return response.data;
  },

  getProjectDetails: async (projectId) => {
    const response = await api.get(`/operations/projects/${projectId}`);
    return response.data;
  },

  updateProjectStatus: async (projectId, status, notes) => {
    const response = await api.put(`/operations/projects/${projectId}/status`, {
      status,
      notes,
    });
    return response.data;
  },

  // Vendor management for operations
  getVendorPerformance: async (vendorId) => {
    const response = await api.get(`/operations/vendors/${vendorId}/performance`);
    return response.data;
  },

  approveVendorBid: async (bidId, notes = '') => {
    const response = await api.post(`/operations/bids/${bidId}/approve`, { notes });
    return response.data;
  },

  rejectVendorBid: async (bidId, reason) => {
    const response = await api.post(`/operations/bids/${bidId}/reject`, { reason });
    return response.data;
  },

  // Resource management
  getResourceAllocation: async (projectId) => {
    const response = await api.get(`/operations/projects/${projectId}/resources`);
    return response.data;
  },

  updateResourceAllocation: async (projectId, resources) => {
    const response = await api.put(`/operations/projects/${projectId}/resources`, {
      resources,
    });
    return response.data;
  },
};

// ===== ENHANCED CUSTOMER PORTAL =====
export const customerPortalAPI = {
  // Customer profile management
  getCustomerProfile: async (customerId) => {
    const response = await api.get(`/customers/${customerId}/profile`);
    return response.data;
  },

  updateCustomerProfile: async (customerId, profileData) => {
    const response = await api.put(`/customers/${customerId}/profile`, profileData);
    return response.data;
  },

  // Order history and tracking
  getOrderHistory: async (customerId) => {
    const response = await api.get(`/customers/${customerId}/orders`);
    return response.data;
  },

  getOrderDetails: async (orderId) => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },

  // Communication
  getMessages: async (customerId) => {
    const response = await api.get(`/customers/${customerId}/messages`);
    return response.data;
  },

  sendMessage: async (recipientId, message, messageType = 'general') => {
    const response = await api.post('/messages', {
      recipientId,
      message,
      messageType,
    });
    return response.data;
  },

  // Document management
  uploadDocument: async (customerId, formData) => {
    const response = await api.post(`/customers/${customerId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getDocuments: async (customerId) => {
    const response = await api.get(`/customers/${customerId}/documents`);
    return response.data;
  },
};

// ===== ADVANCED ADMIN APIS =====
export const adminAPI = {
  // System configuration
  getSystemConfig: async () => {
    const response = await api.get('/admin/system/config');
    return response.data;
  },

  updateSystemConfig: async (configData) => {
    const response = await api.put('/admin/system/config', configData);
    return response.data;
  },

  // Advanced reporting
  getAdvancedReports: async (reportType, filters = {}) => {
    const response = await api.get(`/admin/reports/${reportType}`, { params: filters });
    return response.data;
  },

  generateReport: async (reportConfig) => {
    const response = await api.post('/admin/reports/generate', reportConfig);
    return response.data;
  },

  // Bulk operations
  bulkUpdateUsers: async (userUpdates) => {
    const response = await api.post('/admin/users/bulk-update', { userUpdates });
    return response.data;
  },

  bulkImportData: async (dataType, formData) => {
    const response = await api.post(`/admin/import/${dataType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Audit logs
  getAuditLogs: async (filters = {}) => {
    const response = await api.get('/admin/audit-logs', { params: filters });
    return response.data;
  },
};

// ===== SECURITY & MFA APIs =====
export const securityAPI = {
  // MFA Management (for all roles, not just vendors)
  getMFAStatus: async () => {
    const response = await api.get('/security/mfa/status');
    return response.data;
  },

  setupMFA: async () => {
    const response = await api.post('/security/mfa/setup');
    return response.data;
  },

  confirmMFA: async (token) => {
    const response = await api.post('/security/mfa/confirm', { token });
    return response.data;
  },

  verifyMFA: async (token) => {
    const response = await api.post('/security/mfa/verify', { token });
    return response.data;
  },

  disableMFA: async (token) => {
    const response = await api.post('/security/mfa/disable', { token });
    return response.data;
  },

  // Security dashboard
  getSecurityDashboard: async () => {
    const response = await api.get('/security/dashboard');
    return response.data;
  },

  getSecurityAlerts: async (severity) => {
    const response = await api.get('/security/audit/alerts', {
      params: { severity },
    });
    return response.data;
  },

  // Rate limiting info
  getRateLimitStatus: async () => {
    const response = await api.get('/security/rate-limit/status');
    return response.data;
  },
};

// ===== ML & AI APIs (for non-vendor roles) =====
export const aiAPI = {
  // Customer recommendations
  getCustomerRecommendations: async (customerId, customerProfile) => {
    const response = await api.post('/recommendation/customer-recommendations', {
      customerId,
      customerProfile,
    });
    return response.data;
  },

  // Price predictions
  getPricePredictions: async (costItemId, projectParams) => {
    const response = await api.post('/ai/predict-price', {
      costItemId,
      projectParams,
    });
    return response.data;
  },

  // Market trends
  getMarketTrends: async () => {
    const response = await api.get('/ai/market-trends');
    return response.data;
  },

  // Customer insights for sales team
  getCustomerInsights: async (customerId) => {
    const response = await api.post('/ai/customer-insights', {
      customerId,
      analysisType: 'comprehensive',
    });
    return response.data;
  },
};

// ===== DYNAMIC PRICING API =====
export const dynamicPricingAPI = {
  // Get vendor bids for a specific cost item
  getBidsByCostItem: async (costItemId) => {
    try {
      const response = await api.get(`/vendors/bids/by-cost-item/${costItemId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bids for cost item:', error);
      return [];
    }
  },

  // Calculate dynamic prices for multiple options
  calculateOptionPrices: async (optionIds, housePlanId) => {
    try {
      const response = await api.post('/vendors/pricing/calculate-option-prices', {
        optionIds,
        housePlanId
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating option prices:', error);
      return { optionPrices: [] };
    }
  },

  // Get cost item mappings for an option
  getCostItemMappings: async (optionId) => {
    try {
      const response = await api.get(`/data/cost-item-mappings/${optionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching cost item mappings:', error);
      return [];
    }
  },

  // Get total customer pricing (used by both Sales and Customer interfaces)
  getCustomerTotalPricing: async (customerId) => {
    try {
      const response = await api.get(`/customers/${customerId}/pricing/total`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer total pricing:', error);
      return null;
    }
  },

  // Get customer selection pricing breakdown (unified view for Sales/Customer)
  getCustomerSelectionPricing: async (customerId, includePricingSource = false) => {
    try {
      const response = await api.get(`/customers/${customerId}/selections/pricing`, {
        params: { includePricingSource }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching customer selection pricing:', error);
      return null;
    }
  }
};

// ===== REAL-TIME/WEBSOCKET SUPPORT =====
export const realtimeAPI = {
  // Get real-time data snapshots when WebSocket isn't available
  getBidUpdates: async (filters = {}) => {
    const response = await api.get('/websocket/bid-updates', { params: filters });
    return response.data;
  },

  getProjectUpdates: async (projectId) => {
    const response = await api.get(`/websocket/project-updates/${projectId}`);
    return response.data;
  },

  getNotifications: async () => {
    const response = await api.get('/websocket/notifications');
    return response.data;
  },

  markNotificationRead: async (notificationId) => {
    const response = await api.post(`/websocket/notifications/${notificationId}/read`);
    return response.data;
  },
};

// ===== ENHANCED AUTHENTICATION =====
export const authAPI = {
  // Password management
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  requestPasswordReset: async (email) => {
    const response = await api.post('/auth/password-reset/request', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/password-reset/confirm', {
      token,
      newPassword,
    });
    return response.data;
  },

  // Session management
  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    const response = await api.post('/auth/refresh', { refreshToken });
    
    // Update stored tokens
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    
    return response.data;
  },

  // Account security
  getLoginHistory: async () => {
    const response = await api.get('/auth/login-history');
    return response.data;
  },

  revokeAllSessions: async () => {
    const response = await api.post('/auth/revoke-all-sessions');
    return response.data;
  },
};

export default api;